'use client';

// The one canonical button for the whole app (v5 design system), CRM and portal
// alike (ui-kit, portaalplan U1; components/HomeV2/Button.js re-exports it). Single solid
// fill, no border EXCEPT `white` (the bordered neutral). No colored-text variants
// (rule 4): solid fills carry white text, surfaces carry ink. `active` gives a
// pressed-in toggle look; an async onClick shows a spinner then a check/cross
// INSIDE the button. `pbAssist` is the branded assistant action: the kobalt fill
// plus the P& mark, reserved for AI/assistant actions. Never a dark fill (owner
// rule 2 Oct 2026, DESIGN-SYSTEM.md Regel 37: no black buttons, no exception).
//
// Compose: <Button variant="danger" size="sm" onClick={fn}>Verwijderen</Button>

import React, { useState } from 'react';
import { HV2 } from './tokens.mjs';
import { hashNav } from './primitives.js';
// Het P&-merk (pbAssist) is de gedeelde inline-SVG uit ./marks.js, in currentColor.
import { PMark } from './marks.js';

// Gedempte hardwaretinten - fel kobalt/rood/groen schreeuwt op het grijze
// chassis. Kept local so the precise button look never drifts; HV2 carries the
// matching hexes for cards/chips that share them.
export const BTN_T = {
  ink: '#1c1c1e', onInk: '#f2f2f3', inkHover: '#33333a',
  accent: '#2d4f9e', accentHover: '#24417f',
  danger: '#b03434', dangerHover: '#962c2c',
  success: '#2e7d4f', successHover: '#266841', successTint: 'rgba(46,125,79,0.12)',
  subtle: '#e7e7e8', subtleHover: '#dcdcdd',
  // Een 'white'-knop ligt vlak in het paneel. Lees de gedeelde paneelkleur
  // rechtstreeks zodat een paletwijziging geen tweede knopkleur achterlaat.
  surface: HV2.bg,
  text: '#0f0f11',
};

// Per-variant idle recipe + hover background. No colored-text variants: every
// solid fill carries white text, every surface carries ink. `primary` is the
// kobalt brand action; `pbAssist` is the same kobalt with the P& mark.
const VARIANTS = {
  primary:  { bg: BTN_T.accent,   fg: '#ffffff',  bgh: BTN_T.accentHover },
  accent:   { bg: BTN_T.accent,   fg: '#ffffff',  bgh: BTN_T.accentHover },
  danger:   { bg: BTN_T.danger,   fg: '#ffffff',  bgh: BTN_T.dangerHover },
  success:  { bg: BTN_T.success,  fg: '#ffffff',  bgh: BTN_T.successHover },
  subtle:   { bg: BTN_T.subtle,   fg: BTN_T.text, bgh: BTN_T.subtleHover },
  white:    { bg: BTN_T.surface,  fg: BTN_T.text, bgh: BTN_T.subtle },
  raised:   { bg: BTN_T.surface,  fg: BTN_T.text, bgh: BTN_T.subtle },
  pbAssist: { bg: BTN_T.accent,   fg: '#ffffff',  bgh: BTN_T.accentHover },
};

// `raised` (eigenaarsbesluit 23 augustus 2026, lichte /research): dezelfde witte
// vulling als `white`, maar met een echte lichtval in plaats van het gegraveerde
// pb-btn-relief. Bedoeld voor de losse icoonknoppen op de paarkaarten, waar een
// knop zonder label alleen door zijn schaduw als knop leest.
//
// De ingedrukte staat loopt via React en niet via :active, want een inline
// boxShadow wint altijd van de stylesheet: zonder eigen staat zou de knop nooit
// terugveren. Zie DESIGN-SYSTEM.md regel 8.
const RAISED_SHADOW = '0 1px 2px rgba(0,0,0,0.12)';


// xs (18 september 2026): de knop in een rasterrij van 25px (Sollicitaties).
// Icoon-alleen is size="xs" met style={{ width: 21, padding: 0 }}.
const SIZE = {
  xs:   { height: 21, padding: '0 8px', fontSize: HV2.fsText, gap: 5, icon: 13 },
  sm:   { height: 26, padding: '0 10px', fontSize: HV2.fsText, gap: 6, icon: 14 },
  md:   { height: 30, padding: '0 13px', fontSize: HV2.fsText, gap: 6, icon: 15 },
  lg:   { height: 34, padding: '0 15px', fontSize: HV2.fsText, gap: 7, icon: 16 },
  icon: { height: 28, width: 28, padding: 0, fontSize: HV2.fsText, gap: 0, icon: 16 },
};

// The .pb-btn rules + pb-spin keyframe live in ui-kit/base.css (always present).

function Spinner({ c }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.4" strokeLinecap="round" style={{ animation: 'pb-spin 0.7s linear infinite' }}>
      <path d="M21 12a9 9 0 1 1-6.2-8.6" />
    </svg>
  );
}
const Check = ({ c }) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>;
const Cross = ({ c }) => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;

// `flash` (standaard aan): na een async onClick kleurt de knop 1,2s groen met een
// vinkje (of rood met een kruis). Dat past bij een handeling die TER PLEKKE klaar
// is (opslaan, versturen). Een knop die alleen iets OPENT (een formulier, een
// venster) zet flash={false}: de spinner blijft, maar het groene vinkje niet,
// want dat las als 'goedgekeurd' terwijl er nog niets was ingevuld
// (eigenaarsmelding 8 september 2026, 'Kwalificatie aanmaken').
export default function Button({
  variant = 'primary', size = 'md', active = false, icon = null, children,
  href, onClick, disabled = false, loading = false, flash = true, title, type = 'button', style = {}, fullWidth = false, ...rest
}) {
  const [st, setSt] = useState('idle'); // idle | loading | success | error
  const [pressed, setPressed] = useState(false);
  const v = VARIANTS[variant] || VARIANTS.primary;
  const sz = SIZE[size] || SIZE.md;
  const raised = variant === 'raised';

  const busy = loading || st === 'loading';
  const flashBg = st === 'success' ? BTN_T.success : st === 'error' ? BTN_T.danger : null;
  // A light-variant toggle reads as "pressed" via the grey subtle fill plus the
  // .pb-on inset (eigenaarsregel 16 aug 2026: geen kobalt of blauwe tint op
  // schakelaars; kobalt is voor acties). Multi-selects en aan/uit-filters
  // gebruiken dit, enkelvoudige keuzes gaan via SegControl.
  const lightToggle = active && (variant === 'white' || variant === 'subtle');
  const bg = flashBg || (lightToggle ? BTN_T.subtle : v.bg);
  const fg = flashBg ? '#ffffff' : (lightToggle ? BTN_T.text : v.fg);
  const bgh = flashBg ? bg : (lightToggle ? BTN_T.subtleHover : v.bgh);

  const handle = (e) => {
    if (disabled || busy) return;
    if (!onClick) return;
    let r;
    try { r = onClick(e); } catch (err) { setSt('error'); setTimeout(() => setSt('idle'), 1500); return; }
    if (r && typeof r.then === 'function') {
      setSt('loading');
      if (!flash) { r.then(() => setSt('idle')).catch(() => setSt('idle')); return; }
      r.then(() => { setSt('success'); setTimeout(() => setSt('idle'), 1200); })
        .catch(() => { setSt('error'); setTimeout(() => setSt('idle'), 1500); });
    }
  };

  const content = <>
      {busy ? <Spinner c={fg} />
        : st === 'success' ? <Check c="#ffffff" />
          : st === 'error' ? <Cross c="#ffffff" />
            : (icon || (variant === 'pbAssist' ? <PMark height={13} title="" /> : null))}
      {children != null && <span style={{ display: 'inline-flex', alignItems: 'center' }}>{children}</span>}
    </>;
  const shared = {
    title,
    className: `pb-btn${active ? ' pb-on' : ''}`,
    style: {
      height: sz.height, ...(sz.width ? { width: sz.width } : {}), padding: sz.padding, fontSize: sz.fontSize, gap: sz.gap,
      ...(fullWidth ? { width: '100%' } : {}),
      '--pb-bg': bg, '--pb-fg': fg, '--pb-bgh': bgh,
      ...(raised ? {
        '--pb-bdc': HV2.border,
        boxShadow: pressed ? 'none' : RAISED_SHADOW,
        transform: pressed ? 'translateY(1px)' : 'none',
      } : {}),
      ...(href ? { textDecoration: 'none' } : {}),
      ...style,
    },
    ...(raised ? {
      onPointerDown: () => setPressed(true),
      onPointerUp: () => setPressed(false),
      onPointerLeave: () => setPressed(false),
    } : {}),
    ...rest,
  };
  if (href && !disabled && !busy) return <a {...(onClick ? hashNav(href, handle) : { href })} {...shared}>{content}</a>;
  return <button type={type} disabled={disabled || busy} onClick={handle} {...shared}>{content}</button>;
}
